import { useRef, useEffect, useState } from "react";
import type { AuthUser } from "../types/index.js";
import UserAvatar from "./UserAvatar.js";
import { apiPost } from "../lib/api.js";
import WebhookGuideModal from "./WebhookGuideModal.js";
import ThemeToggle from "./ThemeToggle.js";
import { useTheme } from "../hooks/useTheme.js";

interface UserInfoProps {
	user: AuthUser;
	onLogout: () => void;
}

/**
 * Sidebar footer: clickable avatar that opens an upward dropdown menu
 * with identity details, admin actions, and sign-out.
 */
export default function UserInfo({ user, onLogout }: UserInfoProps) {
	const { preference, setPreference, density, setDensity } = useTheme();
	const [isOpen, setIsOpen] = useState(false);
	const [isRebuilding, setIsRebuilding] = useState(false);
	const [webhookGuideOpen, setWebhookGuideOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);

	const toggle = () => setIsOpen((prev) => !prev);

	const handleRebuildKnowledgeBase = async () => {
		const confirmed = window.confirm(
			"This will destroy all indexed documents across every linked project and rebuild the knowledge base from scratch. This action is irreversible.\n\nAre you sure?"
		);
		if (!confirmed) return;

		setIsRebuilding(true);
		try {
			const response = await apiPost("/api/admin/rebuild-knowledge-base", {});
			if (!response.ok) {
				const data = await response.json().catch(() => ({})) as Record<string, unknown>;
				const message =
					typeof data.message === "string" ? data.message :
					typeof data.error === "string" ? data.error :
					`Request failed with status ${response.status}`;
				alert(`Failed to rebuild knowledge base: ${message}`);
				return;
			}
			const data = await response.json() as Record<string, unknown>;
			const count = data.projectsReset ?? 0;
			const chunks = data.totalChunksDeleted ?? 0;
			alert(`Knowledge base rebuilt: ${count} project(s) reset, ${chunks} chunk(s) deleted. Documents will be re-indexed on the next scheduled run.`);
			setIsOpen(false);
		} catch (err) {
			const message = err instanceof Error ? err.message : "An unexpected error occurred.";
			alert(`Failed to rebuild knowledge base: ${message}`);
		} finally {
			setIsRebuilding(false);
		}
	};

	// Close on click outside
	useEffect(() => {
		if (!isOpen) return;

		function handleClickOutside(e: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		}

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isOpen]);

	// Close on Escape and return focus to trigger
	useEffect(() => {
		if (!isOpen) return;

		function handleKeyDown(e: KeyboardEvent) {
			if (e.key === "Escape") {
				setIsOpen(false);
				triggerRef.current?.focus();
			}
		}

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [isOpen]);

	return (
		<>
		<div className="user-menu" ref={containerRef}>
			{/* Avatar trigger */}
			<button
				ref={triggerRef}
				className="user-menu-trigger"
				onClick={toggle}
				type="button"
				aria-haspopup="menu"
				aria-expanded={isOpen}
				aria-label="Account menu"
			>
				<UserAvatar user={user} size={32} />
				<span className="user-menu-trigger-name">{user.displayName}</span>
				<svg
					className={`user-menu-chevron${isOpen ? " user-menu-chevron--open" : ""}`}
					width="12"
					height="12"
					viewBox="0 0 12 12"
					fill="none"
					aria-hidden="true"
				>
					<path d="M2 8L6 4L10 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			</button>

			{/* Dropdown — opens upward */}
			{isOpen && (
				<div className="user-menu-dropdown" role="menu" aria-label="Account options">
					{/* Identity header */}
					<div className="user-menu-identity">
						<UserAvatar user={user} size={40} />
						<div className="user-menu-identity-text">
							<span className="user-menu-name">{user.displayName}</span>
							<span className="user-menu-email">{user.email}</span>
						</div>
					</div>

					<div className="user-menu-divider" role="separator" />

					{/* Admin section */}
					{user.isAdmin === true && (
						<>
							<span className="user-menu-section-label">Admin</span>
							<button
								className="user-menu-item"
								type="button"
								role="menuitem"
								title="Resets all project knowledge bases for re-ingestion"
								disabled={isRebuilding}
								onClick={handleRebuildKnowledgeBase}
							>
								{isRebuilding ? "Rebuilding..." : "Rebuild Knowledge Base"}
							</button>
							<div className="user-menu-divider" role="separator" />
						</>
					)}

					{/* Resources section */}
					<span className="user-menu-section-label">Resources</span>
					<button
						className="user-menu-item"
						type="button"
						role="menuitem"
						title="View the Zoho Projects webhook configuration guide"
						onClick={() => {
							setIsOpen(false);
							setWebhookGuideOpen(true);
						}}
					>
						Webhook Guide
					</button>

					<div className="user-menu-divider" role="separator" />

					{/* Appearance preferences */}
					<span className="user-menu-section-label">Appearance</span>
					<div className="user-menu-theme-toggle" role="group" aria-label="Appearance preferences">
						<ThemeToggle
							preference={preference}
							onPreferenceChange={setPreference}
							density={density}
							onDensityChange={setDensity}
						/>
					</div>

					<div className="user-menu-divider" role="separator" />

					{/* Sign out */}
					<button
						className="user-menu-item user-menu-item--danger"
						type="button"
						role="menuitem"
						onClick={() => {
							setIsOpen(false);
							onLogout();
						}}
					>
						Sign out
					</button>
				</div>
			)}
		</div>

		{/* Webhook guide modal — portal-rendered, outside the dropdown */}
		{webhookGuideOpen && (
			<WebhookGuideModal onClose={() => setWebhookGuideOpen(false)} />
		)}
		</>
	);
}
